// SPDX-License-Identifier: BUSL-1.1

/**
 * Budget utilization governance card — shows top budget scopes by spend
 * with percentage bars and warning indicators.
 *
 */

import type * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/lib/api-client';

interface BudgetScope {
  scope: string;
  spent: number;
  limit: number;
}

interface BudgetSummaryResponse {
  scopes: BudgetScope[];
}

const WARNING_THRESHOLD = 0.8;

export function GovernanceBudgetCard(): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['budget', 'summary'],
    queryFn: () => apiFetch<BudgetSummaryResponse>('/budget/summary'),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Budget Utilization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const scopes = (data?.scopes ?? [])
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 3);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Budget Utilization</CardTitle>
        <Link
          to="/agents"
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          Details →
        </Link>
      </CardHeader>

      <CardContent className="space-y-3">
        {scopes.length === 0 ? (
          <p className="py-4 text-center text-xs text-neutral-500">
            No budget scopes configured.
          </p>
        ) : (
          scopes.map((scope) => {
            const pct = scope.limit > 0 ? scope.spent / scope.limit : 0;
            const isWarning = pct >= WARNING_THRESHOLD;
            const barColor = isWarning ? 'bg-amber-500' : 'bg-green-500';
            const textColor = isWarning ? 'text-amber-400' : 'text-neutral-300';

            return (
              <div key={scope.scope} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="truncate text-xs text-neutral-400">
                    {scope.scope}
                  </span>
                  <span className={`font-mono text-xs ${textColor}`}>
                    ${scope.spent.toFixed(2)} / ${scope.limit.toFixed(2)}
                    {isWarning && (
                      <span className="ml-1 text-amber-400" title="Above 80% utilization">
                        !
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-800">
                  <div
                    className={`h-full rounded-full transition-all ${barColor}`}
                    style={{ width: `${Math.min(pct * 100, 100)}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
